import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, where, orderBy, limit, startAfter, documentId } from 'firebase/firestore';
import { db } from '../firebase';
import CsvImporter from '../components/CsvImporter';
import AddContactRequestForm from '../components/AddContactRequestForm';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import {
  Box, Typography, Paper, CircularProgress, Alert, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, MenuItem, Tabs, Tab, Chip,
  Dialog, DialogTitle, DialogContent
} from '@mui/material';
import LeadDiagnosesManager from '../components/LeadDiagnosesManager';

const PAGE_SIZE = 50;

const getDateFromValue = (value) => {
  if (!value) return null;
  if (value.toDate) return value.toDate();
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return new Date(value);
  }
  return new Date(value);
};

const formatDate = (value) => {
  const date = getDateFromValue(value);
  if (!date || Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString('es-CL');
};

const LEAD_COLORS = {
  Externo: { bg: '#2196f3', label: 'Externo' },
  Manual: { bg: '#2196f3', label: 'Manual' },
  Web: { bg: '#2196f3', label: 'Web' },
  Presupuesto: { bg: '#ff9800', label: 'Presupuesto' },
  'Agendamiento Online': { bg: '#4caf50', label: 'Agendamiento Online' },
  Diagnóstico: { bg: '#f44336', label: 'Diagnóstico' },
};

const getLeadColor = (source) => LEAD_COLORS[source] || { bg: '#9e9e9e', label: source || 'N/A' };

const ContactRequests = () => {
  const { userData, currentUser } = useAuth();
  const { activeBranchId, isAllBranches, targetBranchIds } = useBranch();
  const isDerivador = userData?.role === 'derivador';

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [activeView, setActiveView] = useState(isDerivador ? 'intake' : 'management');
  const [showManualForm, setShowManualForm] = useState(false);
  const [showDiagnosesDialog, setShowDiagnosesDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [needFilter, setNeedFilter] = useState('all');
  const [goalFilter, setGoalFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const isManager = ['admin', 'Gerente Sucursal', 'Director Médico'].includes(userData?.role);
  const currentEmail = userData?.email || currentUser?.email;
  const defaultAssignedFilter = isManager ? 'all' : 'me';
  const [assignedToFilter, setAssignedToFilter] = useState(defaultAssignedFilter);
  const [nextActionFilter, setNextActionFilter] = useState('all');
  const [kanbanView, setKanbanView] = useState(false);
  const [managementTab, setManagementTab] = useState('active'); // active | paidBudgets | closed
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [patients, setPatients] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [needs, setNeeds] = useState([]);
  const [goals, setGoals] = useState([]);
  const [users, setUsers] = useState([]);
  const [branchResolutions, setBranchResolutions] = useState({});
  const ownersCache = useRef({});

  // Cargar datos maestros una sola vez
  useEffect(() => {
    (async () => {
      try {
        const [patientsData, budgetsData, needsData, goalsData, usersData] = await Promise.all([
          getDocs(collection(db, 'patients')),
          getDocs(collection(db, 'budgets')),
          getDocs(collection(db, 'needs')),
          getDocs(collection(db, 'goals')),
          getDocs(collection(db, 'users')),
        ]);
        setPatients(patientsData.docs.map((d) => ({ ...d.data(), id: d.id })));
        setBudgets(budgetsData.docs.map((d) => ({ ...d.data(), id: d.id })));
        setNeeds(needsData.docs.map((d) => ({ ...d.data(), id: d.id })));
        setGoals(goalsData.docs.map((d) => ({ ...d.data(), id: d.id })));
        setUsers(usersData.docs.map((d) => ({ ...d.data(), id: d.id })));
      } catch (err) {
        console.error('Error cargando maestros:', err);
      }
    })();
  }, []);

  const patientsMap = useMemo(() => {
    return patients.reduce((acc, p) => {
      acc[p.id] = p;
      return acc;
    }, {});
  }, [patients]);

  const budgetsByRequest = useMemo(() => {
    return budgets.reduce((acc, b) => {
      const requestId = b.requestId;
      if (!requestId) return acc;
      if (!acc[requestId]) acc[requestId] = [];
      acc[requestId].push(b);
      return acc;
    }, {});
  }, [budgets]);

  const needsMap = useMemo(() => {
    return needs.reduce((acc, n) => {
      acc[n.name] = n;
      return acc;
    }, {});
  }, [needs]);

  const goalsMap = useMemo(() => {
    return goals.reduce((acc, g) => {
      acc[g.name] = g;
      return acc;
    }, {});
  }, [goals]);

  const usersByEmail = useMemo(() => {
    return users.reduce((acc, u) => {
      if (u.email) acc[u.email] = u;
      return acc;
    }, {});
  }, [users]);

  const getAssignedDisplayName = useCallback((email) => {
    if (!email) return 'Sin asignar';
    const u = usersByEmail[email];
    if (u?.name) return u.name;
    if (u?.email) return u.email;
    return email;
  }, [usersByEmail]);

  const assignedToOptions = useMemo(() => {
    return users
      .filter((u) => u.email)
      .sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email, 'es-CL'));
  }, [users]);

  const getNextActionDueStatus = useCallback((dateString) => {
    if (!dateString) return 'empty';
    const due = new Date(`${dateString}T00:00:00`);
    if (Number.isNaN(due.getTime())) return 'empty';
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const diffDays = Math.round((due - today) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return 'overdue';
    if (diffDays === 0) return 'today';
    if (diffDays <= 7) return 'week';
    return 'future';
  }, []);

  const isNextActionFilterMatch = useCallback((req) => {
    if (nextActionFilter === 'all') return true;
    const status = getNextActionDueStatus(req.nextActionDate);
    if (nextActionFilter === 'overdue') return status === 'overdue';
    if (nextActionFilter === 'today') return status === 'today';
    if (nextActionFilter === 'week') return status === 'today' || status === 'week';
    if (nextActionFilter === 'pending') return status !== 'empty';
    return true;
  }, [nextActionFilter, getNextActionDueStatus]);

  const loadPage = useCallback(async (startAfterDoc = null, append = false) => {
    if (!startAfterDoc) setLoading(true);
    else setLoadingMore(true);

    const resolveBranches = async (docs) => {
      const unresolved = docs.filter((d) => !d.branchId);
      const ownerIdsToFetch = [];
      for (const doc of unresolved) {
        const ownerId = doc.ownerId;
        if (ownerId && !(ownerId in ownersCache.current) && !ownerIdsToFetch.includes(ownerId)) {
          ownerIdsToFetch.push(ownerId);
        }
      }
      const chunkSize = 30;
      for (let i = 0; i < ownerIdsToFetch.length; i += chunkSize) {
        const chunk = ownerIdsToFetch.slice(i, i + chunkSize);
        const ownerSnap = await getDocs(query(collection(db, 'owners'), where(documentId(), 'in', chunk)));
        ownerSnap.docs.forEach((d) => {
          ownersCache.current[d.id] = d.data();
        });
      }
      const resolutions = {};
      for (const doc of unresolved) {
        const patient = doc.patientId ? patientsMap[doc.patientId] : null;
        const owner = doc.ownerId ? ownersCache.current[doc.ownerId] : null;
        const branch =
          doc.branchId ||
          patient?.branchId ||
          (patient?.branchIds || [])[0] ||
          owner?.branchId ||
          (owner?.branchIds || [])[0] ||
          '';
        if (branch) resolutions[doc.id] = branch;
      }
      if (Object.keys(resolutions).length > 0) {
        setBranchResolutions((prev) => ({ ...prev, ...resolutions }));
      }
      return docs;
    };

    const runQuery = async (q) => {
      const snap = await getDocs(q);
      const docs = snap.docs.map((d) => ({ ...d.data(), id: d.id }));
      await resolveBranches(docs);
      setRequests((prev) => (append ? [...prev, ...docs] : docs));
      setLastDoc(snap.docs[snap.docs.length - 1] || null);
      setHasMore(snap.docs.length === PAGE_SIZE);
    };

    try {
      let baseQuery;
      if (!isAllBranches && activeBranchId && activeBranchId !== 'all') {
        baseQuery = query(
          collection(db, 'contact-requests'),
          where('branchId', 'in', [activeBranchId, '']),
          orderBy('createdAt', 'desc'),
          limit(PAGE_SIZE)
        );
      } else {
        baseQuery = query(collection(db, 'contact-requests'), orderBy('createdAt', 'desc'), limit(PAGE_SIZE));
      }
      if (startAfterDoc) baseQuery = query(baseQuery, startAfter(startAfterDoc));

      try {
        await runQuery(baseQuery);
      } catch (branchErr) {
        // Fallback mientras se construye el índice compuesto
        const fallback = startAfterDoc
          ? query(collection(db, 'contact-requests'), orderBy('createdAt', 'desc'), startAfter(startAfterDoc), limit(PAGE_SIZE))
          : query(collection(db, 'contact-requests'), orderBy('createdAt', 'desc'), limit(PAGE_SIZE));
        await runQuery(fallback);
      }
    } catch (err) {
      setError('Error al cargar las solicitudes.');
      console.error(err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [activeBranchId, isAllBranches, patientsMap]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const handleLoadMore = () => {
    if (lastDoc) loadPage(lastDoc, true);
  };

  const refresh = () => {
    setLastDoc(null);
    setHasMore(true);
    loadPage();
  };

  const statusOptions = useMemo(() => {
    const set = new Set();
    requests.forEach((req) => {
      const status = req.status || 'Nuevo';
      if (status) set.add(status);
    });
    return Array.from(set);
  }, [requests]);

  const needOptions = useMemo(() => {
    const set = new Set();
    requests.forEach((req) => {
      const need = req.attentionNeed || '';
      if (need) set.add(need);
    });
    return Array.from(set);
  }, [requests]);

  const goalOptions = useMemo(() => {
    const set = new Set();
    requests.forEach((req) => {
      const goal = req.attentionNeedGoal || needsMap[req.attentionNeed]?.goalName || '';
      if (goal) set.add(goal);
    });
    return Array.from(set);
  }, [requests, needsMap]);

  const sourceOptions = useMemo(() => {
    const set = new Set();
    requests.forEach((req) => {
      const source = req.source || 'Manual';
      if (source) set.add(source);
    });
    return Array.from(set);
  }, [requests]);

  const getEffectiveBranch = (req) =>
    branchResolutions[req.id] ||
    req.branchId ||
    patientsMap[req.patientId]?.branchId ||
    (patientsMap[req.patientId]?.branchIds || [])[0] ||
    (req.ownerId && ownersCache.current[req.ownerId]?.branchId) ||
    (req.ownerId && (ownersCache.current[req.ownerId]?.branchIds || [])[0]) ||
    '';

  const baseFilter = (req) => {
    const term = searchTerm.trim().toLowerCase();
    if (term) {
      const patient = (req.patientName || '').toLowerCase();
      const tutor = (req.ownerName || '').toLowerCase();
      if (!patient.includes(term) && !tutor.includes(term)) return false;
    }
    if (statusFilter !== 'all' && (req.status || 'Nuevo') !== statusFilter) return false;
    if (needFilter !== 'all' && (req.attentionNeed || '') !== needFilter) return false;
    if (goalFilter !== 'all') {
      const reqGoal = req.attentionNeedGoal || needsMap[req.attentionNeed]?.goalName || '';
      if (reqGoal !== goalFilter) return false;
    }
    if (sourceFilter !== 'all' && (req.source || 'Manual') !== sourceFilter) return false;
    if (assignedToFilter === 'me') {
      if (req.assignedTo !== (userData?.email || currentUser?.email)) return false;
    } else if (assignedToFilter === 'unassigned') {
      if (req.assignedTo) return false;
    } else if (assignedToFilter !== 'all' && req.assignedTo !== assignedToFilter) return false;
    if (!isNextActionFilterMatch(req)) return false;
    if (isAllBranches) return true;
    return targetBranchIds.includes(getEffectiveBranch(req));
  };

  const getHasPaidBudget = (req) => budgetsByRequest[req.id]?.some((b) => b.paid === true) || false;

  const filteredRequests = useMemo(() => {
    return requests.filter((req) => {
      const status = req.status || 'Nuevo';
      if (status === 'Cerrado' || status === 'Atendido') return false;
      return baseFilter(req);
    });
  }, [requests, searchTerm, statusFilter, needFilter, goalFilter, sourceFilter, assignedToFilter, nextActionFilter, isNextActionFilterMatch, needsMap, branchResolutions, targetBranchIds, patientsMap, budgetsByRequest, userData, currentUser]);

  const filteredPaidBudgetRequests = useMemo(() => {
    return requests.filter((req) => {
      const status = req.status || 'Nuevo';
      const isAttended = status === 'Atendido';
      if (!getHasPaidBudget(req) && !isAttended) return false;
      return baseFilter(req);
    });
  }, [requests, searchTerm, statusFilter, needFilter, goalFilter, sourceFilter, assignedToFilter, nextActionFilter, isNextActionFilterMatch, needsMap, branchResolutions, targetBranchIds, patientsMap, budgetsByRequest, userData, currentUser]);

  const filteredClosedRequests = useMemo(() => {
    return requests.filter((req) => {
      const status = req.status || 'Nuevo';
      if (status !== 'Cerrado') return false;
      return baseFilter(req);
    });
  }, [requests, searchTerm, statusFilter, needFilter, goalFilter, sourceFilter, assignedToFilter, nextActionFilter, isNextActionFilterMatch, needsMap, branchResolutions, targetBranchIds, patientsMap, budgetsByRequest, userData, currentUser]);

  const getRequestGoal = (req) => req.attentionNeedGoal || needsMap[req.attentionNeed]?.goalName || '';

  const getLastLog = (req) => {
    const logs = req.logs || [];
    if (logs.length === 0) return null;
    return [...logs].sort((a, b) => getDateFromValue(b.timestamp) - getDateFromValue(a.timestamp))[0];
  };

  const getLeadScore = (req) => {
    const sourceScores = { Presupuesto: 30, 'Agendamiento Online': 35, Diagnóstico: 40, Web: 25, Externo: 15, Manual: 10 };
    let score = sourceScores[req.source] || 10;
    if (req.patientType && req.patientType !== 'Sin Convenio') score += 15;
    if (req.diagnosis) score += 20;
    const created = getDateFromValue(req.createdAt);
    const days = created ? Math.max(0, (Date.now() - created.getTime()) / (1000 * 60 * 60 * 24)) : 0;
    if (days > 7) score -= 10;
    else if (days > 3) score -= 5;
    if (req.assignedTo) score += 5;
    return Math.max(0, Math.min(100, Math.round(score)));
  };

  const getScoreColor = (score) => {
    if (score >= 70) return 'success';
    if (score >= 40) return 'warning';
    return 'error';
  };

  const getInattentionDays = (req) => {
    const lastLog = getLastLog(req);
    const last = lastLog ? getDateFromValue(lastLog.timestamp) : getDateFromValue(req.createdAt);
    if (!last) return null;
    return Math.floor((Date.now() - last.getTime()) / (1000 * 60 * 60 * 24));
  };

  const getSLAColor = (days) => {
    if (days === null) return 'default';
    if (days > 2) return 'error';
    if (days > 1) return 'warning';
    return 'success';
  };

  const getDuplicateKey = (req) => {
    const id = req.patientId || req.ownerName || '';
    const source = req.source || 'Manual';
    return `${id}::${source}`;
  };

  const duplicateCounts = useMemo(() => {
    return requests.reduce((acc, req) => {
      const key = getDuplicateKey(req);
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
  }, [requests]);

  const isDuplicate = (req) => (duplicateCounts[getDuplicateKey(req)] || 0) > 1;

  const getGoalProgress = (goalName) => {
    if (!goalName) return 2;
    const real = requests.filter((r) => getRequestGoal(r) === goalName && r.status === 'Atendido').length;
    const target = Number(goalsMap[goalName]?.targetQuantity) || 0;
    if (target > 0) return real / target;
    return real > 0 ? 1.1 : 0;
  };

  const sortedRequests = useMemo(
    () => {
      const copy = [...filteredRequests];
      copy.sort((a, b) => {
        const dateA = getDateFromValue(a.createdAt);
        const dateB = getDateFromValue(b.createdAt);

        if (sortBy === 'progress') {
          const progA = getGoalProgress(getRequestGoal(a));
          const progB = getGoalProgress(getRequestGoal(b));
          if (progA !== progB) return progA - progB;
        }
        if (sortBy === 'goal') {
          const goalA = getRequestGoal(a).toLowerCase();
          const goalB = getRequestGoal(b).toLowerCase();
          if (goalA !== goalB) return goalA.localeCompare(goalB);
        }

        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        return dateB - dateA; // Descendente: más reciente primero
      });
      return copy;
    },
    [filteredRequests, sortBy, requests, needsMap, goalsMap]
  );

  const sortedPaidBudgetRequests = useMemo(
    () => {
      const copy = [...filteredPaidBudgetRequests];
      copy.sort((a, b) => {
        const dateA = getDateFromValue(a.createdAt);
        const dateB = getDateFromValue(b.createdAt);

        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;

        return dateB - dateA;
      });
      return copy;
    },
    [filteredPaidBudgetRequests]
  );

  const sortedClosedRequests = useMemo(
    () => {
      const copy = [...filteredClosedRequests];
      copy.sort((a, b) => {
        const dateA = getDateFromValue(a.createdAt);
        const dateB = getDateFromValue(b.createdAt);

        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;

        return dateB - dateA;
      });
      return copy;
    },
    [filteredClosedRequests]
  );

  const currentManagementRequests =
    managementTab === 'paidBudgets'
      ? sortedPaidBudgetRequests
      : managementTab === 'closed'
        ? sortedClosedRequests
        : sortedRequests;

  const conversionMetrics = useMemo(() => {
    const total = requests.length;
    const withBudget = requests.filter((r) => (budgetsByRequest[r.id] || []).length > 0).length;
    const agendados = requests.filter((r) => r.status === 'Agendado').length;
    const atendidos = requests.filter((r) => r.status === 'Atendido').length;
    const cerrados = requests.filter((r) => r.status === 'Cerrado');
    const closeReasons = cerrados.reduce((acc, r) => {
      const reason = r.closeReason || 'Sin motivo';
      acc[reason] = (acc[reason] || 0) + 1;
      return acc;
    }, {});
    const pct = (n) => (total > 0 ? Math.round((n / total) * 100) : 0);
    return {
      total,
      withBudget,
      agendados,
      atendidos,
      cerrados: cerrados.length,
      closeReasons,
      pct,
    };
  }, [requests, budgetsByRequest]);

  const pipelineStatuses = ['Nuevo', 'Asignado', 'Contactado', 'Presupuesto Enviado', 'Agendado'];

  const kanbanColumns = useMemo(() => {
    return pipelineStatuses.map((status) => ({
      status,
      leads: filteredRequests.filter((r) => (r.status || 'Nuevo') === status),
    }));
  }, [filteredRequests]);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Solicitud de Atención Hospital Clínico Veterinario UDLA</Typography>
      
      {!isDerivador && (
        <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
          <Button
            variant={activeView === 'intake' ? 'contained' : 'outlined'}
            onClick={() => setActiveView('intake')}
          >
            Ingreso de Solicitudes
          </Button>
          <Button
            variant={activeView === 'management' ? 'contained' : 'outlined'}
            onClick={() => setActiveView('management')}
          >
            Gestión de Solicitudes
          </Button>
        </Box>
      )}

      {!isDerivador && activeView === 'management' && statusOptions.length > 0 && (
        <Box sx={{ mt: 1, mb: 2, display: 'flex', justifyContent: 'center' }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, textAlign: 'center' }}
          >
            {statusOptions.map((status, index) => {
              const count = filteredRequests.reduce((acc, req) => {
                const s = req.status || 'Nuevo';
                return s === status ? acc + 1 : acc;
              }, 0);
              const separator = index < statusOptions.length - 1 ? '  ·  ' : '';
              return `${status}: ${count}${separator}`;
            })}
          </Typography>
        </Box>
      )}

      {(activeView === 'intake' || isDerivador) && (
        <>
          {!isDerivador && <CsvImporter onImportComplete={refresh} />}

          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>Solicitud de atención veterinaria</Typography>
            <Button
              variant="contained"
              sx={{ mb: 2 }}
              onClick={() => setShowManualForm((prev) => !prev)}
            >
              {showManualForm ? 'Ocultar formulario' : 'Ingresar Solicitud'}
            </Button>
            {showManualForm && (
              <AddContactRequestForm onAdd={refresh} />
            )}
          </Paper>
        </>
      )}

      {!isDerivador && activeView === 'management' && (
        <Paper sx={{ mt: 3, p: 2 }}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5">Solicitudes de Atención</Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowDiagnosesDialog(true)}
            >
              Configurar diagnósticos
            </Button>
          </Box>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs
              value={managementTab}
              onChange={(_, newValue) => setManagementTab(newValue)}
              textColor="primary"
              indicatorColor="primary"
              variant="fullWidth"
            >
              <Tab value="active" label="Solicitudes activas" />
              <Tab value="paidBudgets" label="Presupuestos pagados" />
              <Tab value="closed" label="Solicitudes cerradas" />
              <Tab value="metrics" label="Métricas" />
            </Tabs>
          </Box>
          {managementTab === 'active' && (
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                variant={kanbanView ? 'outlined' : 'contained'}
                size="small"
                onClick={() => setKanbanView(false)}
              >
                Tabla
              </Button>
              <Button
                variant={kanbanView ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setKanbanView(true)}
              >
                Kanban
              </Button>
            </Box>
          )}
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Buscar"
              size="small"
              variant="outlined"
              placeholder="Buscar por paciente o tutor"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Box>
          <Box
            sx={{
              mt: 1,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 2,
            }}
          >
              <TextField
                select
                size="small"
                variant="outlined"
                label="Necesidad"
                value={needFilter}
                onChange={(e) => setNeedFilter(e.target.value)}
                sx={{ minWidth: 200, flex: '1 1 180px' }}
              >
                <MenuItem value="all">Todas las necesidades</MenuItem>
                {needOptions.map((need) => (
                  <MenuItem key={need} value={need}>
                    {need}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                variant="outlined"
                label="Estado"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="all">Todos los estados</MenuItem>
                {statusOptions.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status || 'Nuevo'}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                variant="outlined"
                label="Meta"
                value={goalFilter}
                onChange={(e) => setGoalFilter(e.target.value)}
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="all">Todas las metas</MenuItem>
                {goalOptions.map((goal) => (
                  <MenuItem key={goal} value={goal}>
                    {goal}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                variant="outlined"
                label="Origen"
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="all">Todos los orígenes</MenuItem>
                {sourceOptions.map((source) => (
                  <MenuItem key={source} value={source}>
                    {source}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                variant="outlined"
                label="Ejecutivo"
                value={assignedToFilter}
                onChange={(e) => setAssignedToFilter(e.target.value)}
                sx={{ minWidth: 200, flex: '1 1 180px' }}
              >
                <MenuItem value="all">Todos los ejecutivos</MenuItem>
                <MenuItem value="me">Mis leads</MenuItem>
                <MenuItem value="unassigned">Sin asignar</MenuItem>
                {assignedToOptions.map((u) => (
                  <MenuItem key={u.email} value={u.email}>
                    {u.name || u.email}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                variant="outlined"
                label="Próx. acción"
                value={nextActionFilter}
                onChange={(e) => setNextActionFilter(e.target.value)}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="all">Todas las fechas</MenuItem>
                <MenuItem value="overdue">Vencidas</MenuItem>
                <MenuItem value="today">Hoy</MenuItem>
                <MenuItem value="week">Próximos 7 días</MenuItem>
                <MenuItem value="pending">Con próxima acción</MenuItem>
              </TextField>
              <TextField
                select
                size="small"
                variant="outlined"
                label="Ordenar por"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="recent">Más recientes</MenuItem>
                <MenuItem value="goal">Meta (A-Z)</MenuItem>
                <MenuItem value="progress">Meta menos avanzada</MenuItem>
              </TextField>
            </Box>
          {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress /></Box>}
          {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
          {!loading && !error && !kanbanView && managementTab !== 'metrics' &&
            <TableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: '#000000' }}>Fecha solicitud</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#000000' }}>Paciente</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#000000' }}>Especie</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#000000' }}>Tutor</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#000000' }}>Teléfono</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#000000' }}>Convenio</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#000000' }}>Origen</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#000000' }}>Necesidad</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#000000' }}>Meta</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#000000' }}>Estado</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#000000' }}>Ejecutivo</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#000000' }}>Próxima acción</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#000000' }}>Fecha</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#000000' }}>Última acción</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#000000' }}>Puntaje</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#000000' }}>SLA</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#000000' }}>Duplicado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentManagementRequests.map((req) => (
                    <TableRow hover key={req.id}>
                      <TableCell>{formatDate(req.createdAt)}</TableCell>
                      <TableCell><Link to={`/contact-requests/${req.id}`}>{req.patientName}</Link></TableCell>
                      <TableCell>{patientsMap[req.patientId]?.species || 'N/A'}</TableCell>
                      <TableCell>{req.ownerName}</TableCell>
                      <TableCell>{req.contactInfo}</TableCell>
                      <TableCell>{req.patientType || 'Sin Convenio'}</TableCell>
                      <TableCell>
                        {(() => {
                          const color = getLeadColor(req.source);
                          return <Chip label={color.label} size="small" sx={{ bgcolor: color.bg, color: '#fff' }} />;
                        })()}
                      </TableCell>
                      <TableCell>{req.attentionNeed}</TableCell>
                      <TableCell>
                        {getRequestGoal(req) ? (
                          <Chip label={getRequestGoal(req)} size="small" color="info" />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>
                        {req.status || 'Nuevo'}
                        {(req.status || 'Nuevo') === 'Atendido' && !getHasPaidBudget(req) && (
                          <Chip
                            label="Sin presupuesto pagado"
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {req.assignedTo ? (
                          <Chip label={getAssignedDisplayName(req.assignedTo)} size="small" />
                        ) : (
                          'Sin asignar'
                        )}
                      </TableCell>
                      <TableCell>
                        {req.nextAction || '—'}
                      </TableCell>
                      <TableCell>
                        {req.nextActionDate ? (
                          (() => {
                            const due = getNextActionDueStatus(req.nextActionDate);
                            const color = due === 'overdue' ? 'error' : (due === 'today' ? 'warning' : (due === 'week' ? 'success' : 'default'));
                            return (
                              <Chip
                                label={req.nextActionDate}
                                size="small"
                                color={color}
                                variant={due === 'overdue' ? 'filled' : 'outlined'}
                              />
                            );
                          })()
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const lastLog = getLastLog(req);
                          if (!lastLog) return '—';
                          return (
                            <Box>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>{lastLog.text}</Typography>
                              <Typography variant="caption" color="text.secondary">{formatDate(lastLog.timestamp)}</Typography>
                            </Box>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const score = getLeadScore(req);
                          return <Chip label={score} size="small" color={getScoreColor(score)} />;
                        })()}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const days = getInattentionDays(req);
                          return days !== null ? (
                            <Chip label={`${days} días`} size="small" color={getSLAColor(days)} />
                          ) : (
                            '—'
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {isDuplicate(req) ? (
                          <Chip label="Posible duplicado" size="small" color="warning" />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          }
          {!kanbanView && managementTab !== 'metrics' && currentManagementRequests.length > 0 && hasMore && (
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? 'Cargando…' : 'Cargar más'}
              </Button>
            </Box>
          )}

          {!loading && !error && kanbanView && managementTab === 'active' && (
            <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', p: 1, minHeight: 400 }}>
              {kanbanColumns.map((col) => (
                <Box key={col.status} sx={{ minWidth: 260, maxWidth: 260, flexShrink: 0 }}>
                  <Box sx={{ bgcolor: '#f5f5f5', borderRadius: 2, p: 1, height: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, px: 1 }}>
                      <Typography variant="subtitle2" fontWeight={700}>{col.status}</Typography>
                      <Chip label={col.leads.length} size="small" color="primary" />
                    </Box>
                    {col.leads.map((req) => (
                      <Paper key={req.id} sx={{ p: 1.5, mb: 1, cursor: 'pointer' }} onClick={() => window.open(`/contact-requests/${req.id}`, '_blank')}>
                        <Typography variant="body2" fontWeight={600} noWrap>{req.patientName}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block" noWrap>{req.ownerName}</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                          <Chip label={getLeadScore(req)} size="small" color={getScoreColor(getLeadScore(req))} />
                          {req.assignedTo && (
                            <Chip label={getAssignedDisplayName(req.assignedTo)} size="small" variant="outlined" />
                          )}
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {!loading && !error && managementTab === 'metrics' && (
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Métricas de conversión</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                {[
                  { label: 'Leads', value: conversionMetrics.total },
                  { label: 'Con presupuesto', value: `${conversionMetrics.withBudget} (${conversionMetrics.pct(conversionMetrics.withBudget)}%)` },
                  { label: 'Agendados', value: `${conversionMetrics.agendados} (${conversionMetrics.pct(conversionMetrics.agendados)}%)` },
                  { label: 'Atendidos', value: `${conversionMetrics.atendidos} (${conversionMetrics.pct(conversionMetrics.atendidos)}%)` },
                  { label: 'Cerrados', value: `${conversionMetrics.cerrados} (${conversionMetrics.pct(conversionMetrics.cerrados)}%)` },
                ].map((m) => (
                  <Paper key={m.label} sx={{ p: 2, minWidth: 140, textAlign: 'center', flex: '1 1 140px' }}>
                    <Typography variant="h4" fontWeight={700}>{m.value}</Typography>
                    <Typography variant="body2" color="text.secondary">{m.label}</Typography>
                  </Paper>
                ))}
              </Box>
              <Typography variant="h6" gutterBottom>Motivos de cierre</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {Object.entries(conversionMetrics.closeReasons).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No hay casos cerrados aún.</Typography>
                ) : (
                  Object.entries(conversionMetrics.closeReasons).map(([reason, count]) => (
                    <Chip key={reason} label={`${reason}: ${count}`} size="small" color="info" />
                  ))
                )}
              </Box>
            </Box>
          )}
        </Paper>
      )}
      <Dialog
        open={showDiagnosesDialog}
        onClose={() => setShowDiagnosesDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Configurar diagnósticos generadores de leads</DialogTitle>
        <DialogContent>
          <LeadDiagnosesManager />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ContactRequests;
